import { Directive, Input, forwardRef } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
/**
 * @example
 *
 * ```html
 *  <input class="input p-b-5-s p-t-7-s"
 *         type="email"
 *         name="email"
 *         required
 *         [validateEmailExists]="hasAccount()"
 *         autocomplete="email"
 *         cy-loginMailInput
 *         [(ngModel)]="email"
 *         [ngModelOptions]="{ updateOn: 'blur' }"
 *         (keyup.enter)="emailInput.blur()"
 *         [muiInput]="'Email address' | translate"
 *         #emailInput>
 * ```
 */
@Directive({
    selector: '[validateEmailExists]',
    standalone: true,
    providers: [
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ValidateEmailExistsDirective),
            multi: true
        }
    ]
})
export class ValidateEmailExistsDirective implements Validator {
    /**
     * @description
     * Tracks changes to the validateEmailExists attribute bound to this directive.
     */
    @Input()
    get validateEmailExists(): boolean {
        return this._validateEmailExists;
    }
    set validateEmailExists(value: boolean) {
        this._validateEmailExists = value != null && value !== false && `${value}` !== 'false';
        if (this._onChange) {
            this._onChange();
        }
    }
    private _validateEmailExists = false;
    private _onChange?: () => void;
    /**
     * @description
     * Method that validates whether the email exists.
     * Returns the validation result if exists, otherwise null.
     *
     * @param {AbstractControl} control
     * @returns {ValidationErrors | null}
     */
    validate(control: AbstractControl): ValidationErrors | null {
        return this.validateEmailExists
            ? {
                  validateEmailExists: false
              }
            : null;
    }
    /**
     * @description
     * Registers a callback function to call when the validator inputs change.
     *
     * @param fn The callback function
     */
    registerOnValidatorChange(fn: () => void): void {
        this._onChange = fn;
    }
}
